import { memo } from 'react'
import { useRouter } from 'next/router'
import { useCookies } from 'react-cookie'

import environment from 'environment'

import { useActionless } from 'redux/hooks'
import { SET_ACTIVE_STATE } from 'redux/active/actions/types'

import Link from 'components/elements/Link'
import Notifications from 'components/misc/GlobalToolbar/Notifications'
import AccountDropdown from 'components/misc/GlobalToolbar/AccountDropdown'
import LogoLink from 'components/misc/GlobalToolbar/LogoLink'
import ThemeDropdown from 'components/misc/GlobalToolbar/ThemeDropdown'
import DecksButton from 'components/misc/GlobalToolbar/DecksButton'
import MobileNavbarOptions from 'components/misc/MobileNavbarOptions '
import ArchidektDropdown from 'components/elements/ArchidektDropdown'
import { Option } from 'components/elements/ArchidektDropdown/types'
import PinnedCards from 'components/misc/GlobalToolbar/PinnedCards'

import { useBuildCollectionUrl } from 'utils/queryParams/collection.queryParams'

import { PUBLIC_ROUTES } from 'utils/token.utils'

import styles from './globalToolbar.module.scss'

type Props = {
  noSearch?: boolean
  noLinks?: boolean
  noAccount?: boolean
  loginRedirectOverride?: string
}

const communityOptions: Option[] = [
  { to: '/community', label: 'Trending', icon: 'angle double up' },
  { to: '/news', label: 'News', icon: 'newspaper outline' },
  { to: '/contest', label: 'Contests', icon: 'trophy' },
  { type: 'spacer' },
  {
    to: '/forum/General/Development',
    label: 'Development',
    icon: 'bug',
    ariaLabel: 'Development forums',
  },
  {
    to: '/forum/General/General',
    label: 'General MTG',
    manaFontIcon: 'ability-d20',
    ariaLabel: 'General forums',
  },
  {
    to: '/forum/General/Archidecture',
    label: 'Deck help',
    icon: 'wrench',
    ariaLabel: 'Deck forums',
  },
  { type: 'spacer' },
  {
    to: 'https://discord.gg/GyAAANU8uJ',
    label: 'Archidekt Discord',
    icon: 'discord',
    ariaLabel: 'Archidekt discord',
    rel: 'noopener noreferrer',
    target: '_blank',
  },
]

const GlobalToolbar = memo(({ noSearch = false, noLinks = false, noAccount = false, loginRedirectOverride }: Props) => {
  const router = useRouter()
  const [{ tbId: userId }] = useCookies(['tbId'])
  const [setActiveState] = useActionless(SET_ACTIVE_STATE)

  const removeLinks = noLinks || (environment.runningOnPrivateServer && PUBLIC_ROUTES.includes(router.pathname))

  const collectionUrl = useBuildCollectionUrl()

  const generateLoginLink = () => {
    if (loginRedirectOverride) return `/login?redirectTo=${encodeURIComponent(loginRedirectOverride)}`
    if (!router || router.asPath === '/') return `/login`

    return `/login?redirectTo=${encodeURIComponent(router.asPath)}`
  }

  return (
    <nav className={`${styles.bar} ${router.route === '/' ? styles.unsticky : ''}`}>
      <div className={styles.content}>
        <div className={styles.left}>
          {!removeLinks && (
            <>
              <MobileNavbarOptions className={styles.hideOnDesktop} />
              <LogoLink />

              {!noSearch && (
                <button
                  className={styles.searchDecksNews}
                  onClick={() => setActiveState({ searchOpen: true })}
                  aria-label="Card Search">
                  Search
                </button>
              )}
              <span className={styles.desktopOptions}>
                <DecksButton />
                {/* <Link href="/community">Community</Link> */}

                <ArchidektDropdown menuClassName={styles.dropdown} options={communityOptions}>
                  <button>Community</button>
                </ArchidektDropdown>

                <Link href={collectionUrl}>Collection</Link>
              </span>
            </>
          )}
        </div>

        <div className={styles.right}>
          <ThemeDropdown className={styles.hideOnMobile} />

          {!noAccount && (
            <>
              {userId && <PinnedCards className={styles.hideOnMobile} />}
              {userId && <Notifications />}
              {!userId ? (
                <>
                  <Link href="/register">Register</Link>
                  <Link href={generateLoginLink()} rel={router.pathname}>
                    Login
                  </Link>
                </>
              ) : (
                <AccountDropdown className={styles.hideOnMobile} />
              )}
            </>
          )}
        </div>
      </div>
    </nav>
  )
})

export default GlobalToolbar
